import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { apiClient } from "../providers/apiClient";

type Guidance = {
  id: string;
  version: number;
  title: string;
  content: string;
  topics: string[];
  sources: { title: string; url: string }[];
  status: "draft" | "reviewed" | "withdrawn";
  reviewNote: string;
};
const blank = (): Guidance => ({
  id: "",
  version: 0,
  title: "",
  content: "",
  topics: ["communication"],
  sources: [{ title: "", url: "" }],
  status: "draft",
  reviewNote: "",
});
const topics = [
  "communication",
  "conflict",
  "connection",
  "boundaries",
  "safety",
  "planning",
];
export function RelationshipLibrary() {
  const query = useQuery({
    queryKey: ["relationship-library"],
    queryFn: () => apiClient.get<Guidance[]>("/admin/chat/knowledge"),
  });
  const [draft, setDraft] = useState<Guidance>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false),
    [saved, setSaved] = useState(false);
  const patch = (data: Partial<Guidance>) => {
    setDraft((current) => (current ? { ...current, ...data } : current));
    setSaved(false);
  };
  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(false);
    setSaved(false);
    try {
      const {
        id,
        version,
        title,
        content,
        topics,
        sources,
        status,
        reviewNote,
      } = draft;
      const body = {
        version,
        title,
        content,
        topics,
        sources,
        status,
        reviewNote,
      };
      const next = await apiClient.put<Guidance>(
        "/admin/chat/knowledge/" + encodeURIComponent(id),
        body,
      );
      setDraft(next);
      setSaved(true);
      await query.refetch();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2} sx={{ maxWidth: 1100 }}>

      <Typography>
        Original guidance with sources and editorial review. Elisa can use
        reviewed entries only. Each save keeps the previous version.
      </Typography>
      {query.isError && (
        <Alert severity="error">Could not load the library.</Alert>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        {query.data?.map((row) => (
          <Button
            key={row.id}
            onClick={() => {
              setDraft({ ...row, sources: row.sources.map((s) => ({ ...s })) });
              setSaved(false);
              setError(false);
            }}
          >
            {row.title} · {row.status} · v{row.version}
          </Button>
        ))}
        <Button variant="outlined" onClick={() => setDraft(blank())}>
          Add guidance
        </Button>
      </Stack>
      {draft && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="Stable identifier"
                value={draft.id}
                disabled={draft.version > 0 || busy}
                helperText="Lowercase words separated by hyphens."
                onChange={(e) => patch({ id: e.target.value })}
              />
              <TextField
                label="Title"
                value={draft.title}
                disabled={busy}
                onChange={(e) => patch({ title: e.target.value })}
              />
              <TextField
                label="Original guidance"
                multiline
                minRows={6}
                value={draft.content}
                disabled={busy}
                helperText="Use your own words. Avoid diagnoses, promised results, or copied source passages."
                onChange={(e) => patch({ content: e.target.value })}
              />
              <TextField
                select
                label="Topics"
                value={draft.topics}
                disabled={busy}
                SelectProps={{ multiple: true }}
                onChange={(e) =>
                  patch({
                    topics:
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                  })
                }
              >
                {topics.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="h6">Sources</Typography>
              {draft.sources.map((source, index) => (
                <Stack
                  key={index}
                  direction={{ xs: "column", sm: "row" }}
                  gap={1}
                >
                  <TextField
                    label="Source title"
                    value={source.title}
                    disabled={busy}
                    onChange={(e) =>
                      patch({
                        sources: draft.sources.map((s, i) =>
                          i === index ? { ...s, title: e.target.value } : s,
                        ),
                      })
                    }
                  />
                  <TextField
                    label="HTTPS source URL"
                    value={source.url}
                    disabled={busy}
                    sx={{ flex: 1 }}
                    onChange={(e) =>
                      patch({
                        sources: draft.sources.map((s, i) =>
                          i === index ? { ...s, url: e.target.value } : s,
                        ),
                      })
                    }
                  />
                  <Button
                    disabled={busy || draft.sources.length === 1}
                    onClick={() =>
                      patch({
                        sources: draft.sources.filter((_, i) => i !== index),
                      })
                    }
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
              <Button
                disabled={busy || draft.sources.length >= 10}
                onClick={() =>
                  patch({ sources: [...draft.sources, { title: "", url: "" }] })
                }
              >
                Add source
              </Button>
              <TextField
                select
                label="Editorial status"
                value={draft.status}
                disabled={busy}
                onChange={(e) =>
                  patch({ status: e.target.value as Guidance["status"] })
                }
              >
                <MenuItem value="draft">Draft — unavailable to Elisa</MenuItem>
                <MenuItem value="reviewed">
                  Reviewed — available to Elisa
                </MenuItem>
                <MenuItem value="withdrawn">
                  Withdrawn — unavailable to Elisa
                </MenuItem>
              </TextField>
              <TextField
                label="Review notes"
                multiline
                minRows={2}
                value={draft.reviewNote}
                disabled={busy}
                helperText="For publication, describe the source check, limitations and editorial review (at least 20 characters). Editorial review does not imply clinical validation."
                onChange={(e) => patch({ reviewNote: e.target.value })}
              />
              {error && (
                <Alert severity="error">
                  Could not save. Check the fields. If someone edited this
                  entry, reopen it before saving.
                </Alert>
              )}
              {saved && (
                <Alert severity="success">Version {draft.version} saved.</Alert>
              )}
              <Button
                variant="contained"
                disabled={
                  busy ||
                  !draft.id ||
                  !draft.title ||
                  draft.content.length < 20 ||
                  (draft.status === "reviewed" && draft.reviewNote.length < 20)
                }
                onClick={() => void save()}
              >
                Save version
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
